import { storedTheme, THEME_STORAGE_KEY, themeChoices } from '@wtfalch/design';
import { product } from './product.ts';

const DARK_THEME = 'afterhours';
const DARK_QUERY = '(prefers-color-scheme: dark)';

// Set once a Settings pick has been made this page load, even when
// `localStorage.setItem` throws (`ThemeSwitch` writes `data-theme` directly
// either way). Without this, a browser blocking site data loses an explicit
// pick back to the system theme on the very next system change, since
// `hasStoredChoice` alone has nothing to read.
let pickedThisLoad = false;

/** Pass as `ThemeSwitch`'s `onChange` so a pick survives `localStorage`
 *  being blocked. */
export function notePickedTheme(): void {
  pickedThisLoad = true;
}

function hasStoredChoice(): boolean {
  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY);
    return raw != null && themeChoices(product).some((choice) => choice.id === raw);
  } catch (error) {
    console.warn('Could not read the stored theme', error);
    return false;
  }
}

function themeFor(dark: boolean): string {
  return dark && themeChoices(product).some((choice) => choice.id === DARK_THEME)
    ? DARK_THEME
    : storedTheme(product);
}

/** Sets `data-theme` on `root`: the Settings choice if there is one, else
 *  `afterhours` while the system is dark and the default otherwise. While
 *  nothing is stored it follows the system; a stored choice, or one picked
 *  this load via `notePickedTheme`, is never overwritten. Returns a function
 *  that stops following. */
export function bootTheme(root: HTMLElement = document.documentElement): () => void {
  pickedThisLoad = false;
  if (hasStoredChoice()) {
    root.dataset.theme = storedTheme(product);
    return () => {};
  }
  const query = window.matchMedia(DARK_QUERY);
  root.dataset.theme = themeFor(query.matches);
  const follow = (event: MediaQueryListEvent) => {
    if (pickedThisLoad || hasStoredChoice()) return;
    root.dataset.theme = themeFor(event.matches);
  };
  query.addEventListener('change', follow);
  return () => query.removeEventListener('change', follow);
}
